/**
 * Árbitro único dos modais promocionais.
 *
 * Antes, cada dono de modal perguntava "o concorrente já está na tela?" com
 * `document.getElementById(...)`. Essa lista foi copiada à mão em 9 blocos
 * espalhados por 4 arquivos, e cada cópia tinha um conteúdo diferente — nenhuma
 * foi atualizada quando as variantes de e-mail apareceram. Resultado: o Popup de
 * Promoção não enxergava `modalSubscribeExitEmail` e a Saída do Assine não
 * enxergava `modalPromotionPopupEmail`, então os dois abriam juntos, empilhados
 * no mesmo z-index.
 *
 * Aqui o estado é um só. Quem quer a tela chama `tryOpen()` e recebe sim ou não.
 * Não há lista para esquecer de atualizar, e a decisão não depende de o
 * concorrente já ter terminado de renderizar.
 */

// Import explícito (os outros stores usam o auto-import do Nuxt): é o que
// permite exercitar o árbitro no runner de teste do Node, que não tem os
// auto-imports. Dentro do Nuxt o comportamento é idêntico.
import { defineStore } from 'pinia'

/** Modais que passam pelo árbitro. */
export type ModalPromocionalId =
  | 'modalPromoMain'
  | 'modalPromoFooter'
  | 'modalPromotionPopupEmail'
  | 'modalSubscribeExit'
  | 'modalSubscribeExitEmail'
  | 'modalNewsletter'
  | 'modalBannerPlanUnique'
  | 'modalNotification'

/**
 * Portões legais, que NÃO passam pelo árbitro e sempre têm precedência.
 *
 * O aviso 18+ e o aceite de cookies são pré-condição de navegação: quando estão
 * na tela, nada mais entra. Eles nunca disputam a vez, então consultá-los no DOM
 * é seguro — só aparecem como bloqueio, nunca como candidato que pode perder uma
 * corrida de renderização.
 */
const PORTOES_LEGAIS = ['modalLegalWarning', 'modalLgpdAccept'] as const

/**
 * QUEM CHEGA PRIMEIRO FICA. Não há preempção por prioridade, de propósito.
 *
 * `tryOpen()` devolvendo `true` é um compromisso: o chamador já liga o
 * `showModal` e começa a desenhar. Deixar um pedido posterior "de maior
 * prioridade" tomar a vez faria os dois aparecerem — que é exatamente o defeito
 * que este árbitro existe para impedir. Desfazer um `true` já entregue exigiria
 * que o perdedor observasse o estado e se escondesse, o que produz modal
 * piscando na tela do visitante.
 *
 * Consequência honesta: ESTE árbitro garante "nunca dois ao mesmo tempo", e NÃO
 * garante qual dos dois ganha quando ambos estão elegíveis — isso continua
 * dependendo de qual API responde primeiro. Tornar o vencedor determinístico é
 * decisão de negócio pendente (ordem entre Saída do Assine e Popup) e exige
 * sequenciar as duas requisições em `default.vue`, em vez de dispará-las em
 * paralelo. É trabalho à parte, não deste PR.
 */

/**
 * RESERVA COM PRAZO.
 *
 * O popup de promoção tem um `banner_timer` — ele espera N segundos antes de
 * decidir se aparece (na campanha no ar, 10). A Saída do Assine decide assim que
 * a API responde, uns 200 ms. Resultado: quando o popup chega para pedir a tela,
 * a Saída do Assine já está lá há quase 10 segundos, e a campanha perde sempre.
 *
 * Decisão de negócio (Thiago, 17/09/2026): havendo promoção ativa, ela tem
 * precedência sobre a Saída do Assine — "o retorno fica ligado direto, o evento
 * é a promoção extra".
 *
 * Por isso a promoção SEGURA a vez antes do timer, e os demais ouvem não nesse
 * intervalo. A reserva tem prazo e vence sozinha: ninguém precisa lembrar de
 * liberar. Isso é de propósito — na primeira versão do árbitro a liberação era
 * obrigatória e cinco modais esqueceram dela, travando a tela pelo resto da
 * navegação. Prazo não se esquece.
 */
function reservaDeOutro(
  state: { reservaDe: ModalPromocionalId | null; reservaAte: number },
  id: ModalPromocionalId
): boolean {
  return (
    state.reservaDe !== null && state.reservaDe !== id && Date.now() < state.reservaAte
  )
}

/**
 * @returns o id do portão legal presente na tela, ou null.
 */
function portaoLegalNaTela(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  return PORTOES_LEGAIS.find((id) => document.getElementById(id) !== null) ?? null
}

export const modalArbiterStore = defineStore('modalArbiter', {
  state: () => ({
    /** Quem está com a tela agora. Um só, nunca mais de um. */
    emCartaz: null as ModalPromocionalId | null,
    /** Quem tem a vez guardada, e até quando (ms epoch). Ver "reserva com prazo". */
    reservaDe: null as ModalPromocionalId | null,
    reservaAte: 0,
  }),

  getters: {
    /** O modal `id` pode se desenhar? Use no `v-if`, se precisar reagir. */
    estaEmCartaz: (state) => {
      return (id: ModalPromocionalId): boolean => state.emCartaz === id
    },
    livre: (state): boolean => state.emCartaz === null,
  },

  actions: {
    /**
     * Pede a tela. Devolve `true` só se puder abrir.
     *
     * Idempotente: pedir de novo o que já está em cartaz devolve `true` sem
     * efeito colateral — o `showModal()` dos componentes roda mais de uma vez.
     *
     * @param  id  modal que quer aparecer
     */
    tryOpen(id: ModalPromocionalId): boolean {
      if (this.emCartaz === id) {
        return true
      }

      if (portaoLegalNaTela() !== null) {
        return false
      }

      if (reservaDeOutro(this, id)) {
        return false
      }

      if (this.emCartaz === null) {
        this.emCartaz = id
        if (this.reservaDe === id) {
          // Reserva cumprida: vira ocupação de verdade.
          this.reservaDe = null
          this.reservaAte = 0
        }
        return true
      }

      // Já tem alguém na tela. Ver o comentário sobre preempção no topo.
      return false
    },

    /**
     * Guarda a vez por um tempo, para quem sabe que vai pedir a tela mas ainda
     * não pode (o popup esperando o `banner_timer`).
     *
     * Não precisa ser desfeita: vence sozinha. Quem reservou e depois desistir
     * não deixa a tela travada — no máximo os outros esperam o prazo acabar.
     *
     * @param  id  quem está guardando a vez
     * @param  ms  por quanto tempo, em milissegundos
     * @returns    false se a tela já está ocupada ou reservada por outro
     */
    segurar(id: ModalPromocionalId, ms: number): boolean {
      if (this.emCartaz !== null) {
        return false
      }

      if (reservaDeOutro(this, id)) {
        return false
      }

      this.reservaDe = id
      this.reservaAte = Date.now() + ms
      return true
    },

    /**
     * Libera a tela. Só quem está em cartaz consegue liberar, para um modal que
     * perdeu a disputa não derrubar o que está aberto ao ser desmontado.
     *
     * @param  id  modal que está saindo
     */
    close(id: ModalPromocionalId): void {
      if (this.emCartaz === id) {
        this.emCartaz = null
      }
    },

    /** Só para teste: devolve o árbitro ao estado inicial. */
    reset(): void {
      this.emCartaz = null
      this.reservaDe = null
      this.reservaAte = 0
    },
  },
})
